import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plug,
  Unplug,
} from 'lucide-react';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  LoadingState,
  PageHeader,
} from '@/components/ui';
import { getErrorMessage } from '@/api/client';
import type { Connection } from '@/api/connections';
import { useConnections, useRevokeConnection } from '@/hooks/useConnections';
import { formatDate } from '@/lib/format-date';
import { cn } from '@/lib/utils';

/**
 * OAuth scope id → locale key under `seller.connections.scopes`. The ids
 * carry the i18next namespace separator (`designdeck:read`), so they cannot
 * be used as keys directly; an unmapped scope falls back to its raw id.
 */
const SCOPE_KEYS: Record<string, string> = {
  'designdeck:read': 'read',
  'designdeck:write': 'write',
  'designdeck:publish': 'publish',
  offline_access: 'offlineAccess',
  openid: 'openid',
};

type Feedback =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

/**
 * Active connections: OAuth grants issued to external clients (e.g.
 * the Claude connector) with a revoke action — revoking disconnects
 * the client and cancels its pending jobs (R26). Rendered inside the
 * SellerShell, which owns the header and navigation.
 */
export function ConnectionsPage() {
  const { t, i18n } = useTranslation();
  const connections = useConnections();
  const revoke = useRevokeConnection();
  const [confirming, setConfirming] = useState<Connection | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const items = connections.data ?? [];

  const connectionLabel = (connection: Connection): string =>
    connection.clientName ?? t('seller.connections.unknownClient');

  const scopeText = (scope: string): string => {
    const key = SCOPE_KEYS[scope];
    return key ? t(`seller.connections.scopes.${key}`) : scope;
  };

  const confirmRevoke = async () => {
    if (!confirming) {
      return;
    }
    const target = confirming;
    setFeedback(null);
    try {
      const result = await revoke.mutateAsync(target.grantId);
      setFeedback({
        kind: 'success',
        message: t('seller.connections.feedback.revoked', {
          name: connectionLabel(target),
          count: result.cancelledJobs,
        }),
      });
    } catch (error) {
      setFeedback({
        kind: 'error',
        message: t('seller.connections.feedback.revokeFailed', {
          message: getErrorMessage(error),
        }),
      });
    } finally {
      // The dialog stays open (loading) until the revoke settles.
      setConfirming(null);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <PageHeader
        eyebrow={t('seller.connections.eyebrow')}
        title={t('seller.connections.title')}
      />

      <p className="max-w-prose text-sm text-mute">
        {t('seller.connections.intro')}
      </p>

      {/* Post-action feedback */}
      {feedback && (
        <Card
          data-testid="revoke-feedback"
          padding="sm"
          role="status"
          className={cn(
            'flex items-center gap-2 text-sm',
            feedback.kind === 'success' ? 'text-success' : 'text-danger'
          )}
        >
          {feedback.kind === 'success' ? (
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {feedback.message}
        </Card>
      )}

      {connections.isLoading ? (
        <div data-testid="connections-skeleton">
          <LoadingState label={t('common.loading')} />
        </div>
      ) : connections.isError ? (
        <Card className="flex flex-col items-center gap-2 text-center text-danger">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          <p className="font-medium">{t('seller.connections.error.title')}</p>
          <p className="text-sm">{getErrorMessage(connections.error)}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => connections.refetch()}
          >
            {t('seller.connections.error.retry')}
          </Button>
        </Card>
      ) : items.length === 0 ? (
        <div data-testid="connections-empty">
          <EmptyState
            icon={<Plug className="h-6 w-6" />}
            title={t('seller.connections.empty.title')}
            description={t('seller.connections.empty.text')}
          />
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((connection) => {
            const revoking =
              revoke.isPending && revoke.variables === connection.grantId;
            return (
              <li key={connection.grantId}>
                <Card
                  padding="none"
                  className="flex items-center gap-5 px-5 py-[18px]"
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control bg-paper text-ink ring-1 ring-line"
                    aria-hidden="true"
                  >
                    <Plug className="h-5 w-5" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h2 className="text-[15px] font-semibold text-ink">
                        {connectionLabel(connection)}
                      </h2>
                      <span className="text-xs text-mute">
                        {t('seller.connections.connectedOn', {
                          date: formatDate(connection.createdAt, i18n.language),
                        })}
                      </span>
                    </div>
                    <ul className="flex flex-wrap gap-1.5">
                      {connection.scopes.map((scope) => (
                        <li
                          key={scope}
                          className="inline-flex h-[22px] items-center rounded-chip bg-paper-3 px-2 text-[11.5px] font-semibold text-ink-2"
                        >
                          {scopeText(scope)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-danger hover:text-danger"
                    onClick={() => setConfirming(connection)}
                    disabled={revoke.isPending}
                  >
                    {revoking ? (
                      <Loader2
                        className="h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Unplug className="h-4 w-4" aria-hidden="true" />
                    )}
                    {t('seller.connections.disconnect')}
                  </Button>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {/* Revoke confirmation */}
      {confirming && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !revoke.isPending) {
              setConfirming(null);
            }
          }}
          title={t('seller.connections.dialog.title', {
            name: connectionLabel(confirming),
          })}
          description={t('seller.connections.dialog.text', {
            name: connectionLabel(confirming),
          })}
          confirmLabel={t('seller.connections.dialog.confirm')}
          cancelLabel={t('seller.connections.dialog.cancel')}
          loadingLabel={t('common.loading')}
          destructive
          loading={revoke.isPending}
          onConfirm={confirmRevoke}
        />
      )}
    </div>
  );
}
